// src/pages/UserProfile.js
import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation} from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { api } from "../api/client";

export default function UserProfile() {
  const navigate = useNavigate();
  const { user, token, updateUser, setActiveCompany } = useContext(AuthContext);

  // Personal details
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savedFirstName, setSavedFirstName] = useState("");
  const [savedLastName, setSavedLastName] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // My company
  const [myCompany, setMyCompany] = useState(null);
  const [changingCompany, setChangingCompany] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companyUpdating, setCompanyUpdating] = useState(false);
  const [companyErr, setCompanyErr] = useState("");
  const [companyOk, setCompanyOk] = useState("");
  const searchTimerRef = useRef(null);

  // Load personal details + employer company
  useEffect(() => {
    if (!token) return;
    api("/users/me", { token })
      .then((data) => {
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setSavedFirstName(data.first_name || "");
        setSavedLastName(data.last_name || "");
        if (data.employer_company_id) {
          setMyCompany({
            id: data.employer_company_id,
            name: data.employer_company_name || "",
            country: data.employer_company_country || "",
            chamber_of_commerce: data.employer_company_coc || "",
            email: data.employer_company_email || "",
            address: data.employer_company_address || "",
            website: data.employer_company_website || "",
            phone: data.employer_company_phone || "",
            position: data.position_in_company || "employee",
          });
        }
      })
      .catch(() => {});
  }, [token]);

  // If redirected back from Companies page after adding a new company, trigger search for it
  const location = useLocation();
  useEffect(() => {
    const { newCompanyId, newCompanyName } = location.state || {};
    if (newCompanyId && newCompanyName) {
      setChangingCompany(true);
      setSearchQuery(newCompanyName);
      setSelectedCompanyId(String(newCompanyId));
      setSearchResults([{ id: newCompanyId, name: newCompanyName, country: "", chamber_of_commerce: "", email: "" }]);
    }
  }, [location.state]);

  async function handleSaveName() {
    if (!firstName.trim() || !lastName.trim()) {
      setSaveErr("First name and last name are required.");
      return;
    }
    setSaveErr("");
    setSaving(true);
    setSaved(false);
    try {
      const updated = await api("/users/me", {
        token,
        method: "PATCH",
        body: { first_name: firstName.trim(), last_name: lastName.trim() },
      });
      updateUser({ first_name: updated.first_name, last_name: updated.last_name });
      setSavedFirstName(updated.first_name);
      setSavedLastName(updated.last_name);
      setSaved(true);
      setEditingName(false);
    } catch (e) {
      setSaveErr(e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    setFirstName(savedFirstName);
    setLastName(savedLastName);
    setSaveErr("");
    setSaved(false);
    setEditingName(false);
  }

  function handleCompanySearch(e) {
    const val = e.target.value;
    setSearchQuery(val);
    setSelectedCompanyId("");
    clearTimeout(searchTimerRef.current);
    if (val.length < 2) { setSearchResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const res = await api(`/companies?q=${encodeURIComponent(val)}`, { token });
        setSearchResults(Array.isArray(res?.companies) ? res.companies : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchBusy(false);
      }
    }, 300);
  }

  async function handleChangeCompany() {
    if (!selectedCompanyId) return;
    setCompanyErr("");
    setCompanyOk("");
    setCompanyUpdating(true);
    try {
      const result = await api("/users/me/company", {
        token,
        method: "PATCH",
        body: { company_id: Number(selectedCompanyId) },
      });
      const selected = searchResults.find(c => String(c.id) === selectedCompanyId);
      setMyCompany({
        id: result.id,
        name: result.name,
        country: selected?.country || "",
        chamber_of_commerce: selected?.chamber_of_commerce || "",
        email: selected?.email || "",
        position: result.position_in_company || "employee",
      });
      setActiveCompany({ id: result.id, name: result.name, role_in_company: "inspector" });
      setCompanyOk(`Company updated to ${result.name}.`);
      setChangingCompany(false);
      setSelectedCompanyId("");
      setSearchQuery("");
      setSearchResults([]);
    } catch (e) {
      setCompanyErr(e?.message || "Failed to update company.");
    } finally {
      setCompanyUpdating(false);
    }
  }

  function handleCancelCompany() {
    setChangingCompany(false);
    setSelectedCompanyId("");
    setSearchQuery("");
    setSearchResults([]);
    setCompanyErr("");
  }

  const inputStyle = {
    border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px",
    fontSize: 14, width: "100%", boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 500, color: "#3a5068", marginBottom: 4,
  };

  const cardStyle = {
    background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px",
  };

  return (
    <div style={{ maxWidth: 512, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>My Profile</h1>

      {/* Section 1 — Personal details */}
      <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Personal details</h2>
          {!editingName ? (
            <button onClick={() => setEditingName(true)}
              style={{ fontSize: 13, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Edit
            </button>
          ) : (
            <button onClick={handleCancelEdit}
              style={{ fontSize: 13, color: "#6b8cae", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Cancel
            </button>
          )}
        </div>

        {editingName ? (
          <>
            <div>
              <label style={labelStyle}>First name</label>
              <input type="text" value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setSaved(false); }}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Last name</label>
              <input type="text" value={lastName}
                onChange={(e) => { setLastName(e.target.value); setSaved(false); }}
                style={inputStyle} />
            </div>
          </>
        ) : (
          <>
            <div>
              <label style={labelStyle}>First name</label>
              <div style={{ fontSize: 14, color: "#1e3a5f", paddingTop: 8, paddingBottom: 8 }}>{firstName || "—"}</div>
            </div>
            <div>
              <label style={labelStyle}>Last name</label>
              <div style={{ fontSize: 14, color: "#1e3a5f", paddingTop: 8, paddingBottom: 8 }}>{lastName || "—"}</div>
            </div>
          </>
        )}

        <div>
          <label style={labelStyle}>Email</label>
          <div style={{ fontSize: 14, color: "#6b8cae", paddingTop: 8, paddingBottom: 8 }}>{user?.email || "—"}</div>
        </div>

        {saveErr && <div style={{ fontSize: 14, color: "#c0392b" }}>{saveErr}</div>}
        {saved && <div style={{ fontSize: 14, color: "#2e7d32" }}>Saved successfully.</div>}

        {editingName && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 4 }}>
            <button onClick={handleSaveName} disabled={saving}
              style={{
                padding: "8px 16px", background: "#1e3a5f", color: "white",
                border: "none", borderRadius: 8, fontSize: 14,
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.5 : 1,
              }}>
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button onClick={() => navigate("/profile/security")}
              style={{ fontSize: 13, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Change email or password →
            </button>
          </div>
        )}
      </div>

      {/* Section 2 — Company details */}
      <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Company details</h2>
          {!changingCompany && (
            <button onClick={() => setChangingCompany(true)}
              style={{ fontSize: 13, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Change
            </button>
          )}
        </div>

        {myCompany ? (
          <div style={{
            background: "white", border: "1px solid #d0dcea", borderRadius: 12,
            padding: 20, display: "flex", flexDirection: "column", gap: 10,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: "#1e3a5f" }}>{myCompany.name}</div>
                <span style={{
                  fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                  padding: "2px 8px", borderRadius: 20,
                  background: myCompany.position === "owner" ? "#e3f0fb" : "#f0f4f8",
                  color: myCompany.position === "owner" ? "#1e5fa8" : "#6b8cae",
                }}>
                  {myCompany.position === "owner" ? "Owner" : myCompany.position === "freelancer" ? "Freelancer" : "Employee"}
                </span>
              </div>
              <button
                onClick={() => navigate(`/companies/${myCompany.id}/edit`)}
                style={{ padding: "4px 12px", border: "1px solid #d0dcea", borderRadius: 6, background: "white", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", color: "#1e3a5f", flexShrink: 0 }}
              >
                Edit
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {myCompany.address && (
                <div style={{ fontSize: 13, color: "#6b8cae" }}>{myCompany.address}</div>
              )}
              {myCompany.country && (
                <div style={{ fontSize: 13, color: "#6b8cae" }}>{myCompany.country}</div>
              )}
              {myCompany.chamber_of_commerce && (
                <div style={{ fontSize: 13, color: "#6b8cae" }}>№ {myCompany.chamber_of_commerce}</div>
              )}
              {myCompany.email && (
                <div style={{ fontSize: 13, color: "#6b8cae" }}>{myCompany.email}</div>
              )}
              {myCompany.phone && (
                <div style={{ fontSize: 13, color: "#6b8cae" }}>{myCompany.phone}</div>
              )}
              {myCompany.website && (
                <a href={myCompany.website} target="_blank" rel="noreferrer"
                  style={{ fontSize: 13, color: "#63b3ed", textDecoration: "none" }}>
                  {myCompany.website}
                </a>
              )}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: 14, color: "#6b8cae", margin: 0 }}>No company set yet.</p>
        )}

        {changingCompany && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={searchQuery}
                onChange={handleCompanySearch}
                placeholder="Search by company name…"
                style={inputStyle}
              />
              {searchBusy && (
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#6b8cae" }}>
                  Searching…
                </span>
              )}
            </div>
            {searchResults.length > 0 && (
              <div style={{ border: "1px solid #d0dcea", borderRadius: 8, background: "white", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                {searchResults.map((c, i) => (
                  <button key={c.id} type="button"
                    onClick={() => setSelectedCompanyId(String(c.id))}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "10px 14px",
                      background: selectedCompanyId === String(c.id) ? "#f0f4f8" : "none",
                      border: "none", borderTop: i > 0 ? "1px solid #e8eef5" : "none",
                      cursor: "pointer", fontSize: 14,
                    }}>
                    <div style={{ fontWeight: 500, color: "#1e3a5f" }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 2 }}>
                      {[c.country, c.chamber_of_commerce && `№ ${c.chamber_of_commerce}`].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={handleChangeCompany}
                disabled={companyUpdating || !selectedCompanyId}
                style={{
                  padding: "8px 16px", background: "#1e3a5f", color: "white",
                  border: "none", borderRadius: 8, fontSize: 14,
                  cursor: (companyUpdating || !selectedCompanyId) ? "not-allowed" : "pointer",
                  opacity: (companyUpdating || !selectedCompanyId) ? 0.5 : 1,
                }}>
                {companyUpdating ? "Saving…" : "Confirm"}
              </button>
              <button onClick={handleCancelCompany}
                style={{ padding: "8px 16px", background: "none", border: "1px solid #d0dcea", borderRadius: 8, fontSize: 14, cursor: "pointer", color: "#6b8cae" }}>
                Cancel
              </button>
              <button onClick={() => navigate("/companies?returnTo=/profile")}
                style={{ fontSize: 13, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", padding: 0, marginLeft: 4 }}>
                Add a new company →
              </button>
            </div>
          </div>
        )}

        {companyErr && <div style={{ fontSize: 14, color: "#c0392b" }}>{companyErr}</div>}
        {companyOk && <div style={{ fontSize: 14, color: "#2e7d32" }}>{companyOk}</div>}
      </div>

      {/* Warning banner */}
      <div style={{
        background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 8,
        padding: 12, display: "flex", gap: 8,
      }}>
        <span style={{ color: "#d97706", fontSize: 18 }}>⚠️</span>
        <p style={{ fontSize: 13, color: "#92400e", margin: 0 }}>
          You are responsible for ensuring the above information is correct.
          Misrepresenting your affiliation with a company may be illegal.
        </p>
      </div>
    </div>
  );
}